import { useEffect, useMemo, useRef, useState } from "react"

import { CalendarDays, ChevronLeft, ChevronRight, Minus } from "lucide-react"

import { Button } from "@/components/ui/button"
import { applyClick, monthGrid, parseIsoDate, toIsoDate, toMeetingRange } from "@/lib/calendar"
import { cn } from "@/lib/utils"
import type { MeetingRange } from "@/types/api"

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]

interface MeetingRangePickerProps {
  /** Receives the picked range; the caller decides what to send. */
  onConfirm: (range: MeetingRange) => void
  disabled?: boolean
}

function formatDay(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
}

/** Month grid for picking a start and end day. Click once for the start, again for the end. */
export function MeetingRangePicker({ onConfirm, disabled = false }: MeetingRangePickerProps) {
  const today = new Date()
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() })
  const [pending, setPending] = useState<{ start: string | null; end: string | null }>({ start: null, end: null })
  const [open, setOpen] = useState(true)
  const rootRef = useRef<HTMLDivElement>(null)

  // Collapse on Escape or a pointer press outside the picker. The selection is kept.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  const todayIso = toIsoDate(today)
  const cells = useMemo(() => monthGrid(view.year, view.month), [view.year, view.month])
  const range = toMeetingRange(pending)
  const monthLabel = new Date(view.year, view.month, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" })
  const summary = range
    ? range.start_date === range.end_date
      ? formatDay(range.start_date)
      : `${formatDay(range.start_date)} bis ${formatDay(range.end_date)}`
    : "Startdatum wählen"

  const shiftMonth = (delta: number) =>
    setView((v) => {
      const next = new Date(v.year, v.month + delta, 1)
      return { year: next.getFullYear(), month: next.getMonth() }
    })

  const inRange = (iso: string) =>
    pending.start !== null && pending.end !== null && iso >= pending.start && iso <= pending.end

  if (!open) {
    return (
      <div ref={rootRef} className="flex justify-start">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          aria-label="Zeitraum auswählen"
          data-testid="meeting-range-reopen"
        >
          <CalendarDays className="size-4" />
          <span className="text-xs">{range ? summary : "Zeitraum wählen"}</span>
        </Button>
      </div>
    )
  }

  return (
    <div ref={rootRef} className="w-full max-w-sm rounded-lg border bg-card p-3 text-sm" data-testid="meeting-range-picker">
      <div className="mb-2 flex items-center justify-between">
        <Button variant="secondary" size="icon" aria-label="Vorheriger Monat" onClick={() => shiftMonth(-1)} disabled={disabled}>
          <ChevronLeft className="size-4" />
        </Button>
        <span className="font-medium" aria-live="polite">
          {monthLabel}
        </span>
        <div className="flex items-center">
          <Button variant="secondary" size="icon" aria-label="Nächster Monat" onClick={() => shiftMonth(1)} disabled={disabled}>
            <ChevronRight className="size-4" />
          </Button>
          <Button variant="ghost" size="icon" aria-label="Zeitraum minimieren" onClick={() => setOpen(false)}>
            <Minus className="size-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground">
        {WEEKDAYS.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((date, index) => {
          if (!date) return <div key={`blank-${index}`} />
          const iso = toIsoDate(date)
          const isEdge = iso === pending.start || iso === pending.end
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              aria-pressed={isEdge}
              aria-label={formatDay(iso)}
              onClick={() => setPending((p) => applyClick(p, iso))}
              className={cn(
                "h-8 rounded-md text-xs hover:bg-accent disabled:opacity-50",
                inRange(iso) && "bg-accent",
                isEdge && "bg-primary text-primary-foreground hover:bg-primary",
                iso === todayIso && !isEdge && "font-semibold underline",
              )}
            >
              {date.getDate()}
            </button>
          )
        })}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground" data-testid="meeting-range-summary">
          {summary}
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setPending({ start: null, end: null })} disabled={disabled || !range}>
            Zurücksetzen
          </Button>
          <Button size="sm" onClick={() => range && onConfirm(range)} disabled={disabled || !range}>
            Bestätigen
          </Button>
        </div>
      </div>
    </div>
  )
}
